/**
 * V-5 — variant price deltas on the pill labels: `Med håndliste · +100 kr`.
 *
 * Design note: docs/engineering/wave6-p3b-price-delta-design.md. Two properties
 * from it are load-bearing and should survive any edit here:
 *
 *   1. Every number shown is a DIFFERENCE between two prices read from the same
 *      variant array the theme uses to set the displayed price. There is no base
 *      price plus a hardcoded increment and no second source of truth, so the
 *      delta and the price cannot drift apart — if the array were stale, both
 *      would be stale identically.
 *
 *   2. A missing combination renders NOTHING. Shopify permits sparse variant
 *      matrices, and a zero would claim "this change is free" when the truth is
 *      "this combination does not exist". The code never synthesises a delta.
 *
 * Deltas are relative to the CURRENT selection, so they legitimately move when
 * another option changes. On a product whose increments are independent they
 * appear fixed; on one where they are not, they shift — and that is correct,
 * because the figure shown is always the true cost of that one change from where
 * the customer is standing.
 *
 * Nothing in the purchase path depends on this file. The suffix is additive text;
 * if this never runs, the pills are exactly what they were.
 */
(() => {
  const SEPARATOR = ' · ';

  /**
   * Shopify's money format, applied by hand because the storefront exposes no
   * formatter: `Shopify.formatMoney` is undefined on the served page and
   * `assets/global.js` keeps its own copy obfuscated and unexported.
   *
   * Handles the four standard placeholders. Amounts arrive in minor units as
   * integers — they are never divided before the split, so there is no float and
   * no rounding drift.
   */
  // Placeholder -> its no-decimals twin. Used to drop `,00` the way Liquid's
  // `money_without_trailing_zeros` does.
  const NO_DECIMALS = {
    amount: 'amount_no_decimals',
    amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
    amount_with_space_separator: 'amount_no_decimals_with_space_separator',
    amount_with_period_and_space_separator: 'amount_no_decimals_with_space_separator',
  };

  const formatMoney = (cents, format) => {
    const value = Math.abs(cents);
    const whole = Math.floor(value / 100);
    const frac = String(value % 100).padStart(2, '0');
    const group = (n, sep) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);

    // V-3 put the PDP price on `money_without_trailing_zeros`, so a delta reading
    // `+100,00 kr` under a price reading `799 kr` would be two formats in one
    // column. This shop's format is `{{amount_with_comma_separator}} kr`, which
    // always carries decimals, so the placeholder is swapped for its no-decimals
    // twin when the amount is whole. Non-whole deltas keep their decimals — the
    // rule is "drop trailing zeros", not "drop precision".
    const resolved = (format || '{{amount}}').replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (match, name) =>
        value % 100 === 0 && NO_DECIMALS[name] ? `{{${NO_DECIMALS[name]}}}` : match
    );

    return resolved.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (_match, name) => {
        switch (name) {
          case 'amount':
            return `${group(whole, ',')}.${frac}`;
          case 'amount_no_decimals':
            return group(whole, ',');
          case 'amount_with_comma_separator':
            return `${group(whole, '.')},${frac}`;
          case 'amount_no_decimals_with_comma_separator':
            return group(whole, '.');
          case 'amount_with_space_separator':
            return `${group(whole, ' ')},${frac}`;
          case 'amount_no_decimals_with_space_separator':
            return group(whole, ' ');
          case 'amount_with_period_and_space_separator':
            return `${group(whole, ' ')}.${frac}`;
          default:
            // An unknown placeholder is left alone rather than guessed at. It
            // will look wrong, which is the point: a silently plausible number is
            // worse than an obviously broken one.
            return _match;
        }
      }
    );
  };

  class VariantPriceDelta {
    constructor(root) {
      this.root = root;
      this.targets = Array.from(root.querySelectorAll('[data-variant-delta]'));
      if (!this.targets.length) return;

      const json = root.querySelector('script[type="application/json"]');
      const source = root.querySelector('[data-money-format]');
      if (!json || !source) return;

      try {
        this.variants = JSON.parse(json.textContent);
      } catch (error) {
        // Malformed JSON leaves every pill exactly as rendered. Deliberately not
        // rethrown: this is decoration on the highest-traffic template we have.
        return;
      }
      if (!Array.isArray(this.variants) || !this.variants.length) return;

      this.moneyFormat = source.dataset.moneyFormat;
      this.render();

      // The theme already publishes this on every selection change, and
      // product-info.js and affiliate-cta.js already consume it — this adds a
      // subscriber to an established channel rather than a new mechanism.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.render());
      } else {
        // Older bundles, or a load-order surprise. `change` on the container
        // catches every radio and select in one listener.
        this.root.addEventListener('change', () => this.render());
      }
    }

    /**
     * The options currently selected, by option position (1-indexed).
     *
     * Position is read from the label bound to each input by `for`/`id`, not by
     * walking up from the input. The pills are FLAT siblings — input, label,
     * input, label — inside one group container, so `closest()` finds nothing and
     * `parentElement.querySelector()` would return the first span of the group
     * regardless of which input was checked. That happens to give the right
     * position and the wrong element, which is the kind of accident that works
     * until the markup changes.
     */
    currentOptions() {
      const inputs = Array.from(
        this.root.querySelectorAll('input[type="radio"]:checked, select')
      );
      const options = [];
      inputs.forEach((input) => {
        if (!input.id && input.tagName !== 'SELECT') return;
        const label = input.id
          ? this.root.querySelector(`label[for="${CSS.escape(input.id)}"]`)
          : null;
        const holder = label ? label.querySelector('[data-option-position]') : null;
        const position = holder ? Number(holder.dataset.optionPosition) : null;
        if (position) options[position - 1] = input.value;
      });
      return options;
    }

    findVariant(options) {
      return this.variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === options.length &&
          variant.options.every((value, index) => value === options[index])
      );
    }

    render() {
      const current = this.currentOptions();
      const currentVariant = this.findVariant(current);

      this.targets.forEach((target) => {
        const position = Number(target.dataset.optionPosition);
        const value = target.dataset.optionValue;

        // No current variant means nothing to measure against. Clear rather than
        // leave a stale figure from the previous selection on screen.
        if (!currentVariant || !position) {
          target.textContent = '';
          return;
        }

        const candidateOptions = current.slice();
        candidateOptions[position - 1] = value;
        const candidate = this.findVariant(candidateOptions);

        // Missing combination, or the option already selected: no suffix. The
        // comp shows none on `Uden håndliste` either.
        if (!candidate || candidate.id === currentVariant.id) {
          target.textContent = '';
          return;
        }

        const delta = candidate.price - currentVariant.price;
        if (delta === 0) {
          target.textContent = '';
          return;
        }

        const sign = delta > 0 ? '+' : '−'; // U+2212 minus, not a hyphen
        target.textContent = `${SEPARATOR}${sign}${formatMoney(delta, this.moneyFormat)}`;
      });
    }
  }

  const init = () => {
    document.querySelectorAll('variant-selects').forEach((root) => {
      if (root.variantPriceDelta) return;
      root.variantPriceDelta = new VariantPriceDelta(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place, which would otherwise leave
  // the new markup unbound.
  document.addEventListener('shopify:section:load', init);
})();
